<script>
import { GlobalBus } from '@services/bus'
import { MODAL } from '@constants/Text'
import {
  MODOS_AGRUPAMENTO,
  MODO_AGRUPAMENTO_AGENTE,
  MODO_AGRUPAMENTO_DEFAULT,
  MODO_AGRUPAMENTO_PASTAS,
  SIDEBAR_CHAT,
} from '@modules/aawz-intelligence-v2/constants/TextChat'
import {
  agruparConversas,
  indexarRotulosPorPersona,
} from '@modules/aawz-intelligence-v2/utils/agrupar-conversas'
import { excluirPasta } from '@modules/aawz-intelligence-v2/utils/conversation-folder-actions'
import { readConversationMovePayload } from '@modules/aawz-intelligence-v2/utils/conversationMoveDrag'
import ChatGroupModeTabs from '@modules/aawz-intelligence-v2/components/chat-group-mode-tabs/chat-group-mode-tabs.vue'
import ChatSidebarGroup from '@modules/aawz-intelligence-v2/components/chat-sidebar-group/chat-sidebar-group.vue'
import ConversationRow from '@modules/aawz-intelligence-v2/components/conversation-row/conversation-row.vue'
import FolderItem from '@modules/aawz-intelligence-v2/components/folder-item/folder-item.vue'

// Linhas do esqueleto de carregando — mesmo espírito de ESQUELETO_RECENTES em
// `chat-sidebar.vue`, mas 3 linhas (o número de hubs) com larguras diferentes,
// para não ler como barras iguais empilhadas (tabela).
const ESQUELETO_GRUPOS = [
  { chave: 'g1', titulo: '62%' },
  { chave: 'g2', titulo: '78%' },
  { chave: 'g3', titulo: '48%' },
]

// Estados mutuamente exclusivos da lista (T029) — mesmo vocabulário e mesma
// precedência de `chat-sidebar.vue`: uma lista JÁ carregada nunca cede lugar a
// um esqueleto/erro por um refresh em voo atrás dela.
const ESTADO_CARREGANDO = 'carregando'
const ESTADO_ERRO = 'erro'
const ESTADO_VAZIO = 'vazio'
const ESTADO_LISTA = 'lista'

/**
 * Corpo da seção "Agrupar por": o segmented dos 4 modos e os grupos que ele
 * produz.
 *
 * Separado de `chat-sidebar.vue` porque é a única parte da sidebar com estado
 * próprio (modo ativo, quais grupos estão abertos, qual está recebendo um
 * drag) — e porque junto o arquivo passava das 500 linhas do repo.
 *
 * O CRUD de PASTA mora aqui e não na sidebar porque o mapa de grupos abertos
 * é deste componente: excluir uma pasta tem de esquecer a chave dela no mesmo
 * passo, e fazer isso de fora exigiria cutucar o filho por `$ref`. As ações de
 * CONVERSA (selecionar, renomear, remover, mover) sobem — quem as dispara
 * também é a lista de Recentes, e o par confirmação + snack tem de ter um dono
 * só.
 *
 * ★ Os 4 estados (T029, US-7) são OPT-IN por prop: `carregando`/`erro`
 * nascem `false` (default seguro para quem montar o componente sem os
 * ligar). `chat-sidebar.vue` os liga com os MESMOS getters que já gatam o
 * esqueleto/erro da seção Recentes (`conversationsLoading`/`foldersLoading`/
 * `conversationsError`) — G-3 do verdict de design: são os dois grupos da
 * MESMA lista de conversas/pastas, então o sinal de carga é um só; inventar
 * um estado próprio para "Agrupar por" divergiria do que a seção Recentes já
 * mostra para os mesmos dados. `@retry` sobe até `chat-sidebar.vue`, que
 * repassa para o `$emit('retry')` que o `view-chat.vue` já escuta.
 */
export default {
  name: 'IntelligenceV2ChatSidebarGroups',
  components: {
    ChatGroupModeTabs,
    ChatSidebarGroup,
    ConversationRow,
    FolderItem,
  },
  props: {
    // Já filtradas pela busca da sidebar.
    conversas: {
      type: Array,
      default: function () {
        return []
      },
    },
    pastas: {
      type: Array,
      default: function () {
        return []
      },
    },
    // Ver a prop de mesmo nome em `chat-sidebar.vue`: apagar pasta recarrega a
    // lista de conversas, e ela precisa sair no MESMO eixo da listagem do pai.
    contextoListagem: {
      type: String,
      default: '',
    },
    agentes: {
      type: Array,
      default: function () {
        return []
      },
    },
    selectedId: {
      type: [String, Number],
      default: null,
    },
    // Os 4 estados da lista (T029) — ver a nota "opt-in" acima do componente.
    carregando: {
      type: Boolean,
      default: false,
    },
    erro: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      modoAtivo: MODO_AGRUPAMENTO_DEFAULT,
      gruposAbertos: {},
      chaveDragOver: '',
      // Id da pasta com exclusão em voo. String e não booleano porque a barra
      // de progresso tem de aparecer NA pasta clicada, não na seção inteira.
      excluindoPastaId: '',
    }
  },
  computed: {
    texto() {
      return SIDEBAR_CHAT
    },
    modos() {
      return MODOS_AGRUPAMENTO
    },
    // `context` guarda a PERSONA; quem o usuário reconhece é o rótulo dela.
    // Índice montado uma vez, e não uma busca por linha renderizada.
    rotuloPorPersona() {
      return indexarRotulosPorPersona(this.agentes)
    },
    // Grupos prontos para render: aberto/fechado, flag de drop-target, estado
    // da exclusão e as LINHAS já rotuladas — o template só lê propriedade.
    grupos() {
      const abertos = this.gruposAbertos
      const modoPastas = this.modoAtivo === MODO_AGRUPAMENTO_PASTAS
      const excluindoId = this.excluindoPastaId
      // Rótulo secundário no modo Pastas (cabeçalho = nome da pasta, o
      // agente não aparece em lugar nenhum da linha) E no modo Agente/Hub
      // ([G-6] do verdict de design) — desde que esse modo passou a agrupar
      // por HUB (`agruparPorHub`, spec-010 [T029]) o cabeçalho da seção
      // deixou de ser o agente e virou o hub; o racional antigo ("o
      // cabeçalho já é o agente") não vale mais, e sem o rótulo o último
      // agente da conversa não aparece em lugar nenhum da linha.
      const rotulos =
        modoPastas || this.modoAtivo === MODO_AGRUPAMENTO_AGENTE
          ? this.rotuloPorPersona
          : null
      const brutos = agruparConversas(this.conversas, this.modoAtivo, {
        pastas: this.pastas,
        agentes: this.agentes,
      })
      return brutos.map(function (grupo) {
        return {
          ...grupo,
          aberto: Boolean(abertos[grupo.chave]),
          // Só "Sem pasta" recebe conversa arrastada para fora de uma pasta;
          // os grupos de pasta têm o drop-target do próprio `folder-item`.
          aceitaSoltas: modoPastas && !grupo.folder,
          excluindo: Boolean(
            grupo.folder && String(grupo.folder.id) === String(excluindoId)
          ),
          linhas: grupo.itens.map(function (conversa) {
            const persona = conversa && conversa.context
            return {
              conversa: conversa,
              rotulo: rotulos ? rotulos[persona] || '' : '',
            }
          }),
        }
      })
    },
    semGrupos() {
      return this.grupos.length === 0
    },
    esqueletoGrupos() {
      return ESQUELETO_GRUPOS
    },
    // ★ O gate NÃO é `conversas.length` — é `semGrupos`. Pastas e Hub SEMPRE
    // produzem grupo (a pasta vazia continua como drop-target; os 3 hubs são
    // um conjunto fixo), então `conversas=[]` sozinho não é "vazio" para
    // estes dois modos — só G6/G7 (Profissional/Cliente, sem dado no payload
    // da conversa) chegam a `semGrupos`. Um `conversas.length` cru aqui
    // apagaria os folder-items (drop-target) sempre que a lista carregada
    // estivesse vazia, quebrando a AC-5.4 (medido: `semGrupos` é a MESMA
    // guarda que o template já usava antes deste estado existir).
    //
    // Dentro desse universo, `conversas` já carregadas vencem um
    // `erro`/`carregando` de um refresh em voo atrás delas — mesma régua de
    // `chat-sidebar.vue`.
    estadoLista() {
      if (!this.semGrupos) return ESTADO_LISTA
      if (this.carregando) return ESTADO_CARREGANDO
      if (this.erro) return ESTADO_ERRO
      return ESTADO_VAZIO
    },
    mostraCarregando() {
      return this.estadoLista === ESTADO_CARREGANDO
    },
    mostraErro() {
      return this.estadoLista === ESTADO_ERRO
    },
    mostraVazio() {
      return this.estadoLista === ESTADO_VAZIO
    },
  },
  methods: {
    alternarGrupo(chave) {
      this.$set(this.gruposAbertos, chave, !this.gruposAbertos[chave])
    },
    selecionarModo(modo) {
      this.modoAtivo = modo
    },
    abrirRenomearPasta(folder) {
      GlobalBus.$emit(MODAL.IA_FOLDER_RENAME, { folder })
    },
    confirmarExcluirPasta(folder) {
      this.$showModal({
        type: 'danger',
        v2: true,
        center: true,
        title: this.texto.excluirPastaTitulo,
        text: this.texto.excluirPastaTexto(folder.name || ''),
        buttons: [
          { text: this.texto.cancelar },
          {
            text: this.texto.excluirPasta,
            action: () => this.removerPasta(folder),
          },
        ],
      })
    },
    // Guard de reentrância + progresso na própria pasta: entre confirmar e o
    // snack não havia sinal nenhum, e confirmar duas vezes disparava dois
    // DELETE com dois recarregamentos concorrentes.
    async removerPasta(folder) {
      if (this.excluindoPastaId) return
      this.excluindoPastaId = String(folder.id)
      try {
        const r = await excluirPasta(folder, {
          context: this.contextoListagem || undefined,
        })
        // Sem isto o mapa guardaria a chave de uma pasta que não existe mais.
        if (r && r.status === 200)
          this.$delete(this.gruposAbertos, 'pasta-' + folder.id)
        // `excluirPasta` devolve `null` para pasta sem id — snack em branco
        // ficaria 8s na tela dizendo nada (mesmo guard de `moverConversa`).
        if (r) this.$showSnack(r)
      } finally {
        this.excluindoPastaId = ''
      }
    },
    onDragOver(grupo, event) {
      if (!grupo.aceitaSoltas) return
      if (event) event.preventDefault()
      this.chaveDragOver = grupo.chave
    },
    onDragLeave() {
      this.chaveDragOver = ''
    },
    onDrop(grupo, event) {
      this.chaveDragOver = ''
      if (!grupo.aceitaSoltas) return
      if (event) event.preventDefault()
      const payload = readConversationMovePayload(
        event ? event.dataTransfer : null
      )
      if (!payload || !payload.conversationId) return
      // Já está sem pasta → no-op, sem request.
      if (!payload.currentFolderId) return
      this.$emit('move', {
        conversationId: payload.conversationId,
        folderId: null,
      })
    },
  },
}
</script>

<template>
  <div class="chat-sidebar-groups">
    <ChatGroupModeTabs
      :modos="modos"
      :modo-ativo="modoAtivo"
      class="mb-2"
      @change="selecionarModo"
    />

    <!-- Carregando (T029): esqueleto com o número de linhas dos hubs, não um
         bloco cinza genérico — mesma régua de `chat-sidebar.vue`/Recentes. -->
    <div v-if="mostraCarregando" class="chat-sidebar-groups__esqueleto">
      <div
        v-for="linha in esqueletoGrupos"
        :key="linha.chave"
        class="chat-sidebar-groups__esqueleto-linha"
      >
        <!-- `BaseLoadingSkeleton` embrulha o bloco num `div` sem largura
             própria — sem um container `flex: 1` em volta, ele encolhe a
             zero como item de flex e a barra some (o mesmo bug já corrigido
             nas linhas de Recentes de `chat-sidebar.vue`). -->
        <div class="chat-sidebar-groups__esqueleto-titulo">
          <BaseLoadingSkeleton :width="linha.titulo" height="12px" />
        </div>
      </div>
    </div>

    <!-- Erro (T029): só toma a tela quando não há grupo nenhum pra mostrar —
         mesma régua de "o erro só aparece sem lista" da seção Recentes. -->
    <div v-else-if="mostraErro" class="ia-sidebar-aux chat-sidebar-groups__aviso">
      <div class="mb-2">{{ texto.erro }}</div>
      <BaseButton underlined min-width="80px" @click="$emit('retry')">
        {{ texto.tentarDeNovo }}
      </BaseButton>
    </div>

    <!-- Vazio (T029) e Lista: a MESMA guarda `semGrupos` de sempre — ela só é
         `true` quando o modo não tem NADA a mostrar (G6/G7; Pastas e Hub
         sempre produzem grupo). `estadoLista === 'vazio'` é essa mesma
         condição, sem `carregando`/`erro`; o texto não muda (`texto.semGrupos`,
         não um `texto.vazio` novo) — reescrever a mensagem aqui inventaria uma
         segunda frase pro mesmo caso que os testes de G6/G7 já cobrem. -->
    <template v-else>
      <div v-if="semGrupos" class="ia-sidebar-aux chat-sidebar-groups__aviso">
        {{ texto.semGrupos }}
      </div>

      <div
        v-for="grupo in grupos"
        :key="grupo.chave"
        class="chat-sidebar-groups__grupo"
        :class="{
          'chat-sidebar-groups__grupo--drag-over': grupo.chave === chaveDragOver,
        }"
        @dragover="onDragOver(grupo, $event)"
        @dragleave="onDragLeave"
        @drop="onDrop(grupo, $event)"
      >
        <template v-if="grupo.folder">
          <FolderItem
            :folder="grupo.folder"
            :count="grupo.itens.length"
            :expanded="grupo.aberto"
            @toggle="alternarGrupo(grupo.chave)"
            @rename="abrirRenomearPasta"
            @delete="confirmarExcluirPasta"
            @drop-conversation="$emit('move', $event)"
          />
          <!-- Progresso NA pasta clicada: é o gatilho que o usuário acionou. -->
          <v-progress-linear
            v-if="grupo.excluindo"
            class="ia-sidebar-progresso"
            indeterminate
            height="2"
          />
          <div v-if="grupo.aberto" class="chat-sidebar-groups__corpo">
            <!-- `rotulo-secundario` só aqui: neste modo o cabeçalho é o nome da
                 PASTA, e sem isto o agente da conversa não aparece em lugar
                 nenhum. No modo Agente o cabeçalho já diz. -->
            <ConversationRow
              v-for="linha in grupo.linhas"
              :key="linha.conversa.id"
              :conversation="linha.conversa"
              :selected="linha.conversa.id === selectedId"
              :folders="pastas"
              :rotulo-secundario="linha.rotulo"
              @select="$emit('select', $event)"
              @rename="$emit('rename', $event)"
              @request-delete="$emit('request-delete', $event)"
              @move="$emit('move', $event)"
            />
            <div
              v-if="!grupo.itens.length"
              class="ia-sidebar-aux chat-sidebar-groups__aviso"
            >
              {{ texto.pastaVazia }}
            </div>
          </div>
        </template>

        <ChatSidebarGroup
          v-else
          :titulo="grupo.titulo"
          :icon-name="grupo.iconName"
          :contador="grupo.itens.length"
          :aberto="grupo.aberto"
          @toggle="alternarGrupo(grupo.chave)"
        >
          <ConversationRow
            v-for="linha in grupo.linhas"
            :key="linha.conversa.id"
            :conversation="linha.conversa"
            :selected="linha.conversa.id === selectedId"
            :folders="pastas"
            :rotulo-secundario="linha.rotulo"
            @select="$emit('select', $event)"
            @rename="$emit('rename', $event)"
            @request-delete="$emit('request-delete', $event)"
            @move="$emit('move', $event)"
          />
        </ChatSidebarGroup>
      </div>
    </template>
  </div>
</template>

<style lang="sass" scoped>
.chat-sidebar-groups__aviso
  padding: 8px

// Esqueleto do estado "carregando" (T029) — mesma régua de
// `.chat-sidebar__esqueleto*` em `chat-sidebar.vue`.
.chat-sidebar-groups__esqueleto
  padding: 4px 0

.chat-sidebar-groups__esqueleto-linha
  display: flex
  align-items: center
  padding: 4px 0

  // Quem cresce é este container — a percentagem do skeleton mede a LINHA,
  // como o título real mede (ver o comentário no template).
  .chat-sidebar-groups__esqueleto-titulo
    flex: 1
    min-width: 0

.chat-sidebar-groups__grupo
  border-radius: var(--radius-sm)

  // Contorno em --neutral-700 (6.98 sobre o fundo quente) e não em cor de
  // marca: o verde dá 2.43 e some contra a superfície.
  &--drag-over
    background: var(--sidebar-warm-hover)
    outline: 2px dashed var(--neutral-700)
    outline-offset: -2px

.chat-sidebar-groups__corpo
  padding-left: 16px
</style>
